import type { UseComponentSizeCallback } from '../../../../utilities/dom';
import type { IconProps } from '@phosphor-icons/react';
import type { FC, KeyboardEvent, ReactElement } from 'react';
import type { RuleSet } from 'styled-components';

import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { memo, useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css, useTheme } from 'styled-components';

import { useMediaQueryContext } from '../../../../utilities/context/dynamic/MediaQueryContext';
import { useNavActionContext } from '../../../../utilities/context/dynamic/NavActionContext';
import { useSearchContext } from '../../../../utilities/context/dynamic/SearchContext';
import { useComponentSize, useWindowSize } from '../../../../utilities/dom';
import { media } from '../../core/styles';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';
import { SiteType } from '~/utilities/graphql/codegen';

const SearchLabelStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Aaf]: css`
    display: none;
  `,
  [SiteType.Employeeshop]: css``,
  [SiteType.Gstar]: css``,
  [SiteType.Outlet]: css``,
  [SiteType.Wahts]: css`
    display: none;
  `,
};

const S = {
  Toggle: styled.button`
    ${({ theme }) => css`
      position: relative;
      height: var(--top-nav-height);
      padding: ${theme.padding.none};
      display: flex;
      align-items: center;

      label {
        color: ${theme.colors.content.secondary};
        margin-left: 10px;
        cursor: pointer;
        text-transform: lowercase;
        font-weight: ${theme.variables.font.weight.regular};

        ${SearchLabelStyles[theme.siteType]}

        @media ${media(theme).lessThan('sm')} {
          display: none;
        }
      }
    `}
  `,
};

interface ToggleSearchV2Props {
  placeholder?: string;
}

const BoldSearchIcon = ({ color, size }: IconProps) => (
  <MagnifyingGlassIcon color={color} size={size} weight="bold" />
);

const ToggleSearchV2IconMap: Record<SiteType, FC<IconProps>> = {
  [SiteType.Aaf]: MagnifyingGlassIcon,
  [SiteType.Employeeshop]: BoldSearchIcon,
  [SiteType.Gstar]: BoldSearchIcon,
  [SiteType.Outlet]: BoldSearchIcon,
  [SiteType.Wahts]: MagnifyingGlassIcon,
};

/**
 * The header search icon of the current site. The mobile TLN drawer renders it too, so the icon
 * keeps its glyph and size when the drawer opens over the header.
 */
export const TopNavigationSearchIcon = (): ReactElement => {
  const { siteType } = useAppContext();
  const theme = useTheme();
  const SearchIcon = ToggleSearchV2IconMap[siteType];

  return (
    <SearchIcon
      color={theme.colors.content.primary}
      size={theme.components.icon.topNavigation.size}
    />
  );
};

const ToggleSearchV2Component = ({ placeholder = '' }: ToggleSearchV2Props) => {
  const ref = useRef<HTMLButtonElement>(null);

  const { isSearchBoxOpen, setIsSearchBoxOpen, setSearchToggleSize } = useSearchContext();
  const { sideNavOpened, setSideNavOpened } = useNavActionContext();
  const { isLargeDesktop } = useMediaQueryContext();
  const { pushToDataLayer } = useDataLayerContext();

  const { width } = useWindowSize({ debounceTime: 150 });

  // save toggle size/position so we can transition to it when opening/closing the search box
  useComponentSize(ref.current, {
    debounceTime: 150,
    callback: useCallback<UseComponentSizeCallback>(
      ([, , defaultCallback]) => defaultCallback,
      // update component size/position when window width changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [width]
    ),
    onResize: setSearchToggleSize,
  });

  const openSearchBox = useCallback(() => {
    if (!isSearchBoxOpen) {
      document.body.classList.add('no-scroll');

      if (sideNavOpened && !isLargeDesktop) {
        setSideNavOpened(false);
      }

      setIsSearchBoxOpen(true);
      pushToDataLayer({ event: 'search_opened', search_method: 'icon_click' });
    }
  }, [
    isSearchBoxOpen,
    sideNavOpened,
    isLargeDesktop,
    setIsSearchBoxOpen,
    setSideNavOpened,
    pushToDataLayer,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if ((e.code === 'Space' || e.code === 'Enter') && !isSearchBoxOpen) {
        e.preventDefault();
        setIsSearchBoxOpen(true);
        pushToDataLayer({ event: 'search_opened', search_method: 'tab_navigation' });
      }
    },
    [isSearchBoxOpen, pushToDataLayer, setIsSearchBoxOpen]
  );

  return (
    <S.Toggle
      ref={ref}
      onClick={openSearchBox}
      className="search-toggle"
      data-testid="search-toggle"
      aria-label={placeholder || ''}
      onKeyDown={handleKeyDown}
    >
      <TopNavigationSearchIcon />
      <label htmlFor="search-input">{placeholder}</label>
    </S.Toggle>
  );
};

export const ToggleSearchV2 = memo(ToggleSearchV2Component, isEqual);
